import React, { useEffect, useState } from 'react';
import {
  DetailModal,
  Divider,
  FileTransfer,
  FileTransferApi,
  FileTransferApiType,
  fileTransferColumns,
  FileTransferTable,
  Flex,
  openNotificationWithIcon,
  Page,
  PermissionGuard,
  RangePicker,
  UploadButton,
  useApiFilters,
  useInterval,
  useUppy,
  useUserinfo,
} from '@mss/mss-component-library';
import { Card, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import Uppy, { UploadResult, UppyFile } from '@uppy/core';
import { DragDrop as DragDropUp, StatusBar } from '@uppy/react';
// @ts-ignore
import Japanese from '@uppy/locales/lib/ja_JP';
import '@uppy/core/dist/style.css';
import '@uppy/drag-drop/dist/style.css';
import 'jsx/file-transfer/scss/statusbar.scss';
import '@uppy/drop-target/dist/style.css';

import { permissionSet } from '../../../authorization/util/permissionSets';
import Breadcrumbs from '../../../common/Breadcrumbs';
import PageSidebar from 'jsx/common/FilterSidebar';

const PAGE_SIZE = 10;

export default function List() {
  const [t] = useTranslation(['fileTransfer', 'common']);
  const [fileTransfers, setFileTransfers] = useState<{
    count: number;
    results: FileTransfer[];
  }>({
    count: 0,
    results: [],
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const [api] = useState<FileTransferApiType>(() => FileTransferApi());
  const [modal, setModal] = useState<FileTransfer | null>(null);
  const [, , user] = useUserinfo();
  const [filtersLoading, setFiltersLoading] = useState(false);

  const updateFileTransfer = (fileTransfer: FileTransfer) => {
    const results = fileTransfers.results;
    const index = results.findIndex(x => fileTransfer.id === x.id);
    results[index] = fileTransfer;
    setFileTransfers({
      ...fileTransfers,
      results: results.filter(x => !x.deleted),
    });
  };
  const filterApi = useApiFilters(t, fileTransfers.count, PAGE_SIZE);

  useEffect(() => {
    async function fetchData() {
      if (!filterApi.dateRangeUrlParams) return;
      setLoading(true);
      try {
        const data = await api.fetchFileTransfers(filterApi.urlParams);
        setFileTransfers(data);
      } catch (e) {
        openNotificationWithIcon(
          notification.error,
          t('Unable to get filetransfer data'),
          String(e)
        );
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [api, filterApi.dateRangeUrlParams, filterApi.urlParams, t]);

  useEffect(() => {
    async function fetchData() {
      if (!filterApi.dateRangeUrlParams) return;
      setFiltersLoading(true);
      try {
        const data = await api.fetchFileTransferFilters(
          filterApi.dateRangeUrlParams
        );
        filterApi.updateFilters(data);
      } catch (e) {
        openNotificationWithIcon(
          notification.error,
          t('Unable to get activitylog filters'),
          String(e)
        );
      } finally {
        setFiltersLoading(false);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterApi.dateRangeUrlParams, filterApi.updateFilters, t]);

  async function pollFileTransfers() {
    if (!filterApi.dateRangeUrlParams) return;
    try {
      const data = await api.fetchFileTransfers(filterApi.urlParams);
      setFileTransfers(data);
    } catch (e) {
      console.error('error fetching fileTransfers', e);
    }
  }

  function cleanup() {
    pollFileTransfers();
    setIsUploading(false);
  }

  useInterval(pollFileTransfers, 3000);

  const uppy = useUppy({
    factory: () =>
      new Uppy({
        id: 'filetransfer',
        meta: { type: 'filetransfer' },
        restrictions: {
          minFileSize: 1,
          maxFileSize: 20 * 1024 * 1024 * 1024,
        },
        autoProceed: true,
        debug: import.meta.env.DEV,
        locale:
          user?.language === 'ja'
            ? Japanese
            : {
                strings: {
                  noDuplicates:
                    "Cannot add the duplicate file '%{fileName}' in the same session. Refresh and try again",
                },
              },
      })
        .on('upload', () => {
          setIsUploading(true);
        })
        .on('file-removed', (file, reason) => {
          console.info('File Removed', { file, reason });
        })
        .on('cancel-all', cleanup)
        .on('upload-pause', cleanup)
        .on('complete', (results: UploadResult) => {
          setIsUploading(false);
          if (results.successful.length > 0) {
            openNotificationWithIcon(
              notification.success,
              t('File upload complete'),
              <ul>
                {results.successful.map((result: UppyFile, i: number) => (
                  <li key={`s${i}`}>{result.name}</li>
                ))}
              </ul>
            );
          }
          if (results.failed.length > 0) {
            openNotificationWithIcon(
              notification.error,
              t('File upload failed'),
              <ul>
                {results.failed.map((result: any, i: number) => (
                  <li key={`e${i}`}>
                    {result.name}
                    <br />
                    {result.error.length > 100
                      ? `${result.error.slice(0, 100)}...`
                      : result.error}
                  </li>
                ))}
              </ul>
            );
          }
        })
        .on('restriction-failed', (file, error) => {
          cleanup();
          openNotificationWithIcon(
            notification.warning,
            t('Restriction'),
            <p>{error.message}</p>
          );
        })
        .on('error', error => {
          cleanup();
          console.error('Upload error', error.message, error);
          openNotificationWithIcon(
            notification.error,
            t('Error'),
            <p>{error.message}</p>
          );
        })
        .on('upload-retry', fileID => {
          console.info('Upload retried:', fileID);
        }),
  });
  return (
    <PermissionGuard redirect codename={permissionSet.fileTransferViewAny}>
      <Page
        breadCrumbs={<Breadcrumbs />}
        padding={false}
        card={false}
        actions={
          <Flex>
            <RangePicker t={t} onChange={filterApi.updateDateRange} />
            <PermissionGuard codename={'add_filetransfer'}>
              <UploadButton
                t={t}
                uppy={uppy}
                theming={api.appContext}
                className={'ml-2'}
              />
            </PermissionGuard>
          </Flex>
        }
        main={
          <>
            <StatusBar
              uppy={uppy}
              hideAfterFinish={false}
              hideRetryButton={false}
              showProgressDetails
            />
            <Card className={'.filetransfer-table'} bodyStyle={{ padding: 0 }}>
              <FileTransferTable
                id="transferTable"
                bordered={false}
                dataSource={fileTransfers.results}
                loading={loading}
                columns={fileTransferColumns(
                  t,
                  api,
                  user,
                  updateFileTransfer,
                  setModal,
                  isUploading
                )}
                rowKey={'id'}
                pagination={filterApi.pagination}
                onChange={filterApi.onChange}
                size="small"
              />
            </Card>

            <DetailModal
              t={t}
              api={api}
              fileTransfer={modal}
              closeModal={() => setModal(null)}
            ></DetailModal>
          </>
        }
        sidebar={
          <>
            <PageSidebar
              loading={filtersLoading}
              availableFilters={filterApi.filters}
              toggleFilter={filterApi.toggleFilter}
              search={{ value: filterApi.search, set: filterApi.setSearch }}
              selectedFilters={filterApi.selectedFilters}
              translationPrefix={['fileTransfer']}
            />
            <PermissionGuard codename={'add_filetransfer'}>
              <div className={'mt-4'}>
                <Divider />
                <div className={'mb-4'}></div>

                <DragDropUp
                  uppy={uppy}
                  locale={{
                    strings: {
                      dropHereOr: t('Drop here or %{browse}'),
                      browse: t('browse'),
                    },
                  }}
                />
              </div>
            </PermissionGuard>
          </>
        }
      />
    </PermissionGuard>
  );
}
